import { useMemo } from 'react';
import { useQuery } from 'react-query';
import * as api from '../../services/api';
import useOptimisticMutation from '../useOptimisticMutation';
import useAccount from './useAccount';

const useGeneralAvailabilities = ({ employeeIds }) => {
  const { accountId } = useAccount();

  const storeKey = ['general-availabilities', accountId, employeeIds];
  const { isLoading, data, refetch } = useQuery(
    storeKey,
    () => api.fetchGeneralAvailabilities({ accountId, employeeIds }),
    { staleTime: 0 }
  );

  const mappedAvailabilitiesByEmployee = useMemo(
    () =>
      (data?.data || []).reduce((output, input) => {
        const { employeeId } = input || {};
        output[employeeId] = input;
        return output;
      }, {}),
    [data?.data]
  );

  const { mutateAsync: addGeneralAvailability } = useOptimisticMutation(
    storeKey,
    ({ boards }) =>
      api.updateGeneralAvailability({
        accountId,
        boards
      }),
    ({ previousData, boards, employeeIds: updateEmployeeIds }) => {
      let newItem;

      if (previousData?.data?.length === 0) {
        newItem = updateEmployeeIds.map((id) => {
          const { status, board } = boards.find(({ employeeId }) => employeeId === id);
          return {
            employeeId: id,
            board,
            status
          };
        });
      } else {
        newItem = previousData?.data.map((item) => {
          if (!updateEmployeeIds.includes(item.employeeId)) {
            return item;
          }

          const { status, board } = boards.find(({ employeeId }) => employeeId === item.employeeId) || {};

          return {
            ...item,
            board,
            status
          };
        });
      }

      return { ...previousData, data: newItem };
    }
  );

  return {
    isLoading,
    generalAvailabilities: data?.data || [],
    addGeneralAvailability,
    mappedAvailabilitiesByEmployee,
    refetch
  };
};

export default useGeneralAvailabilities;
